/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import './LoanResume.scss';
import { FooterContent } from '../../../onboarding/components';
import { LoanOption, Icon } from '../../../../shared/components/ui/atoms';
import { ONBOARDING_STEP_CREDIT_SCORE_KEY, ONBOARDING_STEP_INVESTMENT_EXPERIENCE_KEY, ONBOARDING_STEP_PROPERTY_KIND_KEY, getOnboardingStepTitle, isStaging, toUSD } from '../../../../utils/constants';
import { getInputData } from '../LoanBuilder/utils';
import { useBorrowerOnboarding, useNewOwnershipOnboarding, useBrokerOnboarding } from '../../hooks/useOnboardingMutations'
import { Button } from '../../../borrower/components';
import { isLongTermRental, isRefinance, isShortTermBridge, isShortTermFixAndFlip } from '../../../../utils/functions';
import { TermSheet } from './utils';
import { trackEvent, UserOnboarded, trackSectionLoadedEvent } from '../../../../utils/mixpanel';
import ReactGA from "react-ga4";
import { useOwnership } from '../../context/OwnershipContext';
import { useAuth } from '../../../auth/context/AuthContext';
import { API_GENERATE_PDF, BORROWER_DASHBOARD_URL, ENV_PRODUCTION } from '@common/config/environments';

// Extend Window interface for global functions
declare global {
  interface Window {
    gtag?: (command: string, targetId: string, config?: any) => void;
    send_convertion_event?: () => void;
    parseJwt?: (token: string) => any;
    heap?: {
      identify: (id: string) => void;
      addUserProperties: (properties: any) => void;
    };
  }
}

const DEFAULT_SELECTED_OPTION = {
  ltv: 75,
  loanTerm: "12 months",
  rate: 9.45,
  monthlyPayment: 1181.25,
  totalLoanAmount: 175000,
  caption: 'BEST LTV'
}

const DEFAULT_LOAN_DATA = {
  purchaseValue: 150000,
  cashRequired: 3334.83,
  originationFee: 2.34,
  serviceFee: 999.00,
  proRatedInterest: 300.00,
  thirdPartyCosts: "Contact closing agent",
  pointsCharged: 1,
  additionalDetails: {
    loanType: "1 year fixed",
    amortization: "Interest Only",
    lien: "1st position",
    asIsValue: 200000.00,
    estimatedArv: 300000.00,
    holdbackAmount: 25000
  },
  documents: {
    preApprovalLetterURL: "https://storage.googleapis.com/constlending-borrowers/1704920475430.pdf",
    termSheetURL: "https://storage.googleapis.com/constlending-borrowers/1704920475430.pdf"
  }
}

window.send_convertion_event = () => {
  if (window.gtag) {
    try {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-611686651/FZOgCPPV1dgZEPux1qMC',
        'event_callback': () => { }
      });
    } catch (e) {

    }
  }
}

window.parseJwt = (token: string) => {
  var base64Url = token.split('.')[1];
  var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));

  return JSON.parse(jsonPayload);
}

export const fetchDocumentsData = async (termSheetData: any, userData: any) => {
  const body = {
    "termSheetData": {
      "asIsValue": termSheetData.asIsValue,
      "rehabBudget": termSheetData.constructionReserve,
      "arv": termSheetData.estimatedARV,
      "creditScore": termSheetData.creditScore,
      "investmentExperience": termSheetData.investmentExperience,
      "propertyType": termSheetData.propertyType,
      "loanPurpose": termSheetData.loanPurpose,
      "loanAmount": termSheetData.totalLoanAmountFormatted,
      "advanceAmount": termSheetData.advanceAmount,
      "ltv": termSheetData.ltvDisplay,
      "constructionReserve": termSheetData.constructionReserve,
      "ltc": termSheetData.ltcDisplay,
      "address": termSheetData.address || "1234 Main St",
      "rate": termSheetData.interestRate,
      "term": termSheetData.term,
      "buydown": termSheetData.buydownPoints,
      "dscr": termSheetData.dscr,
      "monthlyRent": termSheetData.stimatedMonthlyRent,
      "annualExpenses": termSheetData.annualExpenses
    },
    "preApprovalLetterData": {
      "firstName": userData.firstName || userData.borrower?.firstName || "Jhon",
      "lastName": userData.lastName || userData.borrower?.lastName || "Doe",
      "address": termSheetData.address || "1234 Main St",
      "ltv": termSheetData.ltvDisplay,
    }
  }

  const res = await fetch(`${API_GENERATE_PDF}/files/generateFiles`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })

  const data = await res.json()

  return data
}

interface InputPlaceholderProps {
  label: string;
  customKey: string;
  values: any;
}

const InputPlaceholder = ({ label, customKey, values }: InputPlaceholderProps) => {
  return (
    <div className='value-item'>
      <p>{label}</p>
      <p>{toUSD(values[customKey], 0)}</p>
    </div>
  )
}

interface LoanResumeProps {
  values: any;
  selectedOption: any;
  userData: any;
  borrowerData: any;
  onBack: () => void;
  isBrokerMode?: boolean;
  isProcessingTeamMode?: boolean;
  [key: string]: any;
}

const LoanResumeComponent = (props: LoanResumeProps) => {
  const { ownershipSelected } = useOwnership();
  const { values, selectedOption, userData, borrowerData, onBack, isBrokerMode, isProcessingTeamMode } = props;
  // Only used in broker mode - safe to call here as hooks must be at top level
  const { user: authUser, isBroker, isAuthenticated } = useAuth();

  const { execute: executeNewBorrower } = useBorrowerOnboarding()
  const { execute: executeOwnership } = useNewOwnershipOnboarding()
  const { execute: executeBroker } = useBrokerOnboarding()

  const [userOAuthToken, setUserOAuthToken] = useState(false)
  const [loanId, setLoanId] = useState(null)
  const [isLoadingOAuth, setIsLoadingOAuth] = useState(false)
  const [shouldExecuteOnContinue, setShouldExecuteOnContinue] = useState(false)
  const [documentsReady, setDocumentsReady] = useState(false)
  const [userClicked, setUserClicked] = useState(false)
  const hasCalledSubmission = useRef(false)
  const lastUserDataRef = useRef<any>(null)

  useEffect(() => {
    trackSectionLoadedEvent('Loan Resume')
  }, [])

  const onSubmission = async (documentsData: any) => {
    if (userOAuthToken) {
      return
    }

    setIsLoadingOAuth(true)

    // Get address from multiple possible sources (userData first, then values as fallback)
    const addressFromUserData = userData?.investmentAddress || userData?.borrower?.investmentAddress;
    const addressFromValuesFormatted = values['Investment property address formatted'];
    const addressFromValues = values['Investment property address'] || values['Main investment property address'];
    const rawAddress = addressFromValuesFormatted || addressFromUserData || (typeof addressFromValues === 'string' ? addressFromValues : '') || '';

    const loanData = {
      address: rawAddress,
      ...values,
      selectedOption,
      investmentExperience: values["How many properties have you purchased/sold in the last 24 months?"].split(" ")[0].toLowerCase(),
      ...documentsData,
      urlParams: window.location.search
    }

    let inputData;

    if (!isBrokerMode) {
      let userAccountData;
      // In processing team mode, if there's no borrowerData, it means "New Borrower" was selected
      // In that case, we should still use CreateLoanWithOwnership but without _id
      if (!borrowerData && !isProcessingTeamMode) {
        userAccountData = {
          name: `${userData.firstName.trim()} ${userData.lastName.trim()}`,
          phone: userData.phoneNumber,
          email: userData.email,
          password: userData.password
        }
      } else if (!borrowerData && isProcessingTeamMode) {
        // New borrower in processing team mode - use CreateUserBorrowerAndLoan which requires password
        userAccountData = {
          name: `${userData.firstName.trim()} ${userData.lastName.trim()}`,
          phone: userData.phoneNumber,
          email: userData.email,
          password: userData.password || "password"
        }
      } else {
        const isSomeoneElse = ownershipSelected?.isNew === true;
        const ownershipFirstName = typeof ownershipSelected?.name === 'string' ? ownershipSelected.name.trim() : '';
        const ownershipLastName = typeof ownershipSelected?.lastname === 'string' ? ownershipSelected.lastname.trim() : '';
        const borrowerName = typeof borrowerData?.name === 'string' ? borrowerData.name.trim() : '';
        const borrowerLastName = typeof borrowerData?.lastName === 'string' ? borrowerData.lastName.trim() : '';
        const borrowerPhone = typeof borrowerData?.phone === 'string' ? borrowerData.phone.trim() : '';

        // If "Someone else" is selected, use guarantor names from values
        const guarantorFirstName = isSomeoneElse ? (values.guarantorFirstName as string) : '';
        const guarantorLastName = isSomeoneElse ? (values.guarantorLastName as string) : '';

        // Priority: userData > guarantor names (if someone else) > ownership name (but not "Someone") > borrower name
        const resolvedFirstName =
          (typeof userData.firstName === 'string' && userData.firstName.trim()) ||
          (typeof guarantorFirstName === 'string' && guarantorFirstName.trim()) ||
          (ownershipFirstName && ownershipFirstName !== 'Someone' ? ownershipFirstName : '') ||
          borrowerName.split(' ')[0] || '';

        const resolvedLastName =
          (typeof userData.lastName === 'string' && userData.lastName.trim()) ||
          (typeof guarantorLastName === 'string' && guarantorLastName.trim()) ||
          (ownershipLastName && ownershipLastName !== 'else' ? ownershipLastName : '') ||
          borrowerLastName ||
          borrowerName.split(' ').slice(1).join(' ') || '';

        const resolvedPhone = (typeof userData.phoneNumber === 'string' && userData.phoneNumber.trim()) || ownershipSelected?.phone || borrowerPhone || '';

        // In processing team mode, use borrowerData _id (from getBorrowers), not ownership _id
        const ownershipId = isProcessingTeamMode
          ? (borrowerData?._id || null)
          : (ownershipSelected?._id || null);

        userAccountData = {
          _id: ownershipId,
          name: `${resolvedFirstName} ${resolvedLastName}`.trim(),
          phone: resolvedPhone,
          email: userData.email,
        }
      }

      inputData = {
        ...userAccountData,
        loanData
      }
    } else {
      // BROKER MODE ONLY: When broker is logged in, use authenticated user data; otherwise use form data
      const isLoggedInBroker = isBroker && isAuthenticated;

      let brokerName, brokerPhone, brokerEmail, brokerPassword;

      if (isLoggedInBroker && authUser) {
        // Use authenticated broker's data
        brokerName = `${authUser.name || ''} ${authUser.lastName || ''}`.trim();
        brokerPhone = authUser.phone || '';
        brokerEmail = (authUser.email || '').trim().toLowerCase();
        brokerPassword = ''; // No password needed for logged in broker
      } else {
        // Use form data for new broker registration
        brokerName = userData.broker ? `${userData.broker.firstName?.trim() || ''} ${userData.broker.lastName?.trim() || ''}`.trim() : '';
        brokerPhone = userData.broker?.phoneNumber || '';
        brokerEmail = (userData.broker?.email || '').trim().toLowerCase();
        brokerPassword = userData.broker?.password || '';
      }

      const userAccountData = {
        name: brokerName,
        phone: brokerPhone,
        email: brokerEmail,
        password: brokerPassword
      }

      const borrowerData = {
        name: `${userData.borrower.firstName.trim()} ${userData.borrower.lastName.trim()}`,
        email: "",
        phone: "",
        password: "",
        loanData
      }

      inputData = {
        ...userAccountData,
        borrowerData
      }
    }
    
    if (ReactGA.isInitialized) {
      ReactGA.set({ userId: inputData.email });

      ReactGA.event('borrower_lead', {
        category: 'onboarding',
        action: 'User completed the onboarding process'
      });

      ReactGA.event('sign_up', {
        category: 'onboarding',
        action: 'User completed the onboarding process'
      });
    }

    if (window.send_convertion_event) {
      window.send_convertion_event()
    }

    let response;

    let token;
    let loanId;

    try {
      // Check if this is a new borrower based on userData.isNewBorrower or borrowerData being null
      const isNewBorrower = userData?.isNewBorrower || !borrowerData

      if (isProcessingTeamMode) {
        // In processing team mode, use CreateUserBorrowerAndLoan for new borrowers, CreateLoanWithOwnership for existing borrowers
        if (isNewBorrower) {
          response = await executeNewBorrower(inputData)
        } else {
          // Ensure we have the borrower _id (from getBorrowers) in the inputData, not ownership _id
          const inputDataWithOwnership = inputData as any;
          if (!inputDataWithOwnership._id && borrowerData?._id) {
            inputDataWithOwnership._id = borrowerData._id;
          }
          response = await executeOwnership(inputDataWithOwnership)
        }
      } else if (!isBrokerMode && isNewBorrower) {
        response = await executeNewBorrower(inputData)
      } else if (!isBrokerMode && !isNewBorrower) {
        response = await executeOwnership(inputData)
      } else {
        response = await executeBroker(inputData)
      }

      const responseData = response?.data?.createUserBorrowerAndLoan || response?.data?.createLoanWithOwnership || response?.data?.onboardBroker
      token = responseData?.token
      loanId = responseData?.loanId

      const heap = window.heap
      const decodedToken = window.parseJwt?.(token);
      const userId = decodedToken.aud;

      if (heap?.identify) {
        const heap = window.heap
        if (userId) {
          heap?.identify(userId);
        }
      }

    } catch (e) {
    } finally {
      setUserOAuthToken(token)
      setLoanId(loanId)
      setIsLoadingOAuth(false)
      trackEvent(UserOnboarded())
    }
  }

  const [documentsURLs, setDocumentsURLs] = useState<{
    preApprovalLetterURL?: string;
    termSheetURL?: string;
    mobileURL?: string;
  }>({})

  const {
    originationPoints,
    totalLoanAmount
  } = selectedOption || {}

  const {
    price: purchaseValue
  } = values

  const inputData = getInputData(values)

  const elements = inputData.map((v) => <InputPlaceholder key={v.customKey} {...v} values={values} />)

  // useEffect para ejecutar automáticamente cuando el token esté disponible
  useEffect(() => {
    if (userOAuthToken && (shouldExecuteOnContinue || userClicked) && loanId) {
      setShouldExecuteOnContinue(false) // Reset para evitar ejecuciones múltiples
      setUserClicked(false) // Reset para evitar ejecuciones múltiples
      window.location.href = `${BORROWER_DASHBOARD_URL}/?oauth=${userOAuthToken}&isNew=true&loanId=${loanId}`
    }
  }, [userOAuthToken, shouldExecuteOnContinue, userClicked, loanId])

  let termSheetData: any = null

  const rawAddressForTermSheet = userData?.investmentAddress || userData?.borrower?.investmentAddress
  const address = rawAddressForTermSheet || ''
  const propertyKindStepTitle = getOnboardingStepTitle(ONBOARDING_STEP_PROPERTY_KIND_KEY)
  const creditScoreStepTitle = getOnboardingStepTitle(ONBOARDING_STEP_CREDIT_SCORE_KEY)
  const investmentExperienceStepTitle = getOnboardingStepTitle(ONBOARDING_STEP_INVESTMENT_EXPERIENCE_KEY)

  const propertyTypeValue =
    (typeof values.propertyType === 'string' && values.propertyType.trim().length > 0
      ? values.propertyType
      : undefined) ||
    (values[propertyKindStepTitle] as string | undefined)

  const creditScoreValue =
    (values[creditScoreStepTitle] as string | undefined) ||
    (typeof values.creditScore === 'string' && values.creditScore.trim().length > 0
      ? values.creditScore
      : undefined)

  const investmentExperienceValue = values[investmentExperienceStepTitle]

  if (isShortTermBridge(values)) {
    termSheetData = TermSheet.constructForBridgeLoan({
      address,
      propertyType: propertyTypeValue ?? '',
      creditScore: creditScoreValue ?? '',
      investmentExperience: typeof investmentExperienceValue === 'string' ? investmentExperienceValue : '',
      price: values.price,
      currentLoanBalance: values.currentLoanBalance,
      cashoutAmount: selectedOption.cashoutAmount,
      ltv: selectedOption.ltv,
      rate: selectedOption.rate,
      totalLoanAmount: selectedOption.totalLoanAmount,
      buydownPoints: selectedOption.buydownPoints
    })
  } else if (isShortTermFixAndFlip(values)) {
    termSheetData = TermSheet.constructForFixAndFlipLoan({
      address,
      propertyType: propertyTypeValue ?? '',
      creditScore: creditScoreValue ?? '',
      investmentExperience: typeof investmentExperienceValue === 'string' ? investmentExperienceValue : '',
      price: values.price,
      currentLoanBalance: values.currentLoanBalance,
      cashoutAmount: selectedOption.cashoutAmount,
      rehabBudget: values.constructionBudget,
      arv: values.afterRepairValue,
      ltv: selectedOption.ltv,
      ltc: selectedOption.ltc,
      rate: selectedOption.rate,
      totalLoanAmount: selectedOption.totalLoanAmount,
      buydownPoints: selectedOption.buydownPoints
    })
  } else if (isLongTermRental(values)) {
    termSheetData = TermSheet.constructForRentalLoan({
      address,
      propertyType: propertyTypeValue ?? '',
      creditScore: creditScoreValue ?? '',
      price: values.price,
      currentLoanBalance: values.currentLoanBalance,
      cashoutAmount: selectedOption.cashoutAmount,
      monthlyRent: values.monthlyRent,
      annualTaxes: values.annualTaxes,
      annualInsurance: values.annualInsurance,
      annualHOA: values.annualHoa,
      dscr: selectedOption.dscr,
      ltv: selectedOption.ltv,
      rate: selectedOption.rate,
      totalLoanAmount: selectedOption.totalLoanAmount,
      buydownPoints: selectedOption.buydownPoints
    })
  }

  useEffect(() => {
    // Reset ref when userData changes to a different value
    if (lastUserDataRef.current !== userData) {
      hasCalledSubmission.current = false;
      lastUserDataRef.current = userData;
    }

    const fun = async () => {
      if (!userOAuthToken && userData && !hasCalledSubmission.current) {
        hasCalledSubmission.current = true;
        const { data: documentsData } = await fetchDocumentsData(termSheetData, userData)
        setDocumentsURLs(documentsData)
        setDocumentsReady(true)
        await onSubmission(documentsData)
      }
    }

    if (userData) {
      fun()
    }

    return () => { }
  }, [userData])

  const onContinue = async () => {
    setUserClicked(true) // Marcar que el usuario hizo click

    if (userOAuthToken) {
      window.location.href = `${BORROWER_DASHBOARD_URL}/?oauth=${userOAuthToken}&isNew=true&loanId=${loanId}`
    } else if (isLoadingOAuth) {
      // Si está cargando, marcar para ejecutar cuando termine
      setShouldExecuteOnContinue(true)
    }
    // Si no hay token y no está cargando, no hacer nada porque el proceso ya se está ejecutando en segundo plano
  }

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  }

  const preApprovalLetterURL = documentsURLs?.preApprovalLetterURL
  const termSheetURL = documentsURLs?.termSheetURL
  const mobileURL = documentsURLs?.mobileURL

  const resolvedFirstName = React.useMemo(() => {
    const candidate =
      userData?.borrower?.firstName ||
      userData?.firstName ||
      borrowerData?.firstName ||
      borrowerData?.borrower?.firstName ||
      ownershipSelected?.name;

    return typeof candidate === 'string' ? candidate.trim() : '';
  }, [userData, borrowerData, ownershipSelected]);

  return (
    <React.Fragment>
      {/* <div className='back-button'>
        <a onClick={handleBack}>
          ← Back to loan builder
        </a>
      </div> */}

      <LoanResumeMobile
        firstName={resolvedFirstName}
        mobileURL={mobileURL}
        selectedOption={selectedOption}
        values={values}
        termSheetData={termSheetData || {}}
        isBrokerMode={isBrokerMode}
        elements={elements}
        onBack={onBack}
        onContinue={onContinue}
        userClicked={userClicked}
        isLoadingOAuth={isLoadingOAuth}
        documentsReady={documentsReady}
        preApprovalLetterURL={preApprovalLetterURL}
        termSheetURL={termSheetURL}
      />
    </React.Fragment>
  );
}

LoanResumeComponent.displayName = 'LoanResume';

export const LoanResume = React.memo(LoanResumeComponent);

interface LoanInfoBoxProps {
  selectedOption: any;
  values: any;
  termSheetData: any;
}

const LoanInfoBox = ({
  selectedOption,
  values,
  termSheetData
}: LoanInfoBoxProps) => {

  const {
    closingCashAmount,
    closingCash: cashRequired,
    originationFee,
    originationFeePercentage,
    serviceFee,
    proRatedInterest,
    buydownFee,
    buydowFeePercentage,
    downPayment,
    _buydownPoints: originationPoints,
    totalLoanAmount,
    purchaseValue,
    rentalFee,
    rentalFeeAmount,
    advanceAmount,
    thirdPartyCosts,
    legalCosts,
    loanType,
    amortization,
    lien,
    asIsValue,
    estimatedARV,
    constructionReserve,
    dscr
  } = termSheetData


  return (
    <>
      <div className='summary-row'>
        <span>Total loan amount</span>
        <span>{toUSD(totalLoanAmount, 0)}</span>
      </div>
      <div className='summary-row'>
        <span>Rate</span>
        <span> {selectedOption.rate}%</span>
      </div>
      <div className='summary-row'>
        <span>Loan term</span>
        <span>{selectedOption.loanTerm}</span>
      </div>
      <div className='summary-row'>
        <span>Monthly payment</span>
        <span>{toUSD(selectedOption.monthlyPayment, 0)}</span>
      </div>

      <div className='summary-row summary-row--additional-details'>
        <div>
          <div>
            {
              closingCashAmount < 0 ?
                "Est. Cash To Be Received At Closing"
                :
                "Est. Cash Required At Closing"
            }
          </div>
          <span>
            {cashRequired}
          </span>
        </div>
        <div>
          {
            !isRefinance(values) ?
              <div>
                <p>
                  Down Payment
                </p>
                <span>
                  {downPayment}
                </span>
              </div>
              :
              null
          }
          <div>
            <p>
              Origination Fee
            </p>
            <span>
              {`${originationFee} (${originationFeePercentage})`}
            </span>
          </div>
          <div>
            <p>
              Buydown Fee
            </p>
            <span>
              {`${buydownFee} (${originationPoints ?? 0}%)`}
            </span>
          </div>
          <div>
            <p>
              Processing Fee
            </p>
            <span>
              {serviceFee}
            </span>
          </div>
          {
            isLongTermRental(values) ?
              <div>
                <p>
                  Property Taxes and Insurance Reserves
                </p>
                <span>
                  {rentalFee}
                </span>
              </div>
              :
              null
          }
          <div>
            <p>
              Third-party Costs
            </p>
            <span>
              {thirdPartyCosts}
            </span>
          </div>
          <div>
            <p>
              Est. Legal Costs
            </p>
            <span>
              {legalCosts}
            </span>
          </div>
        </div>
      </div>
      <div className='summary-row summary-row--additional-details' style={{ marginBottom: 40 }}>
        <div>
          Additional Details
        </div>
        <div>
          <div>
            <p>Loan Type</p>
            <span>{termSheetData.loanTypeDisplay || loanType}</span>
          </div>
          <div>
            <p>Amortization</p>
            <span>{amortization}</span>
          </div>
          {
            isShortTermFixAndFlip(values) ?
              <div>
                <p>Advance Amount</p>
                <span>{advanceAmount}</span>
              </div>
              :
              null
          }
          {
            isShortTermFixAndFlip(values) ?
              <div>
                <p>Construction Reserve</p>
                <span>{constructionReserve}</span>
              </div>
              :
              null
          }
          <div>
            <p>Lien</p>
            <span>{lien}</span>
          </div>
          <div>
            <p>As-is Value</p>
            <span>{asIsValue}</span>
          </div>
          {
            isLongTermRental(values) ?
              <div>
                <p>DSCR</p>
                <span>{dscr}</span>
              </div>
              :
              null
          }
        </div>
      </div>
      <div className='separator' style={{ height: 1 }}></div>

    </>
  )
}

interface DocumentsBoxProps {
  preApprovalLetterURL?: string;
  termSheetURL?: string;
}

const DocumentsBox = ({ preApprovalLetterURL, termSheetURL }: DocumentsBoxProps) => (
  <div className='documents documents--resume'>
    <p>Documents</p>
    <div>
      <div>
        <div>
          Pre-approval letter
        </div>
        <span className={"download"}>
          <a target={"_blank"} rel="noreferrer" href={preApprovalLetterURL}>
            {"Download now  --> "}
          </a>
        </span>
      </div>
      <div>
        <div>
          Term sheet
        </div>
        <span className={"download"}>
          <a target={"_blank"} rel="noreferrer" href={termSheetURL}>
            {"Download now  --> "}
          </a>
        </span>
      </div>
    </div>
  </div>
)

interface ConfirmationBoxProps {
  isBrokerMode?: boolean;
}

const ConfirmationBox = ({ isBrokerMode }: ConfirmationBoxProps) => (
  <div className='confirmation-box'>
    <div className='left'>
      <Icon svgFilePath={"./assets/icons/alert-triangle.svg"} />
    </div>
    <div className='right'>
      {
        !isBrokerMode ?
          <p>Within the next 24 hours, we&apos;ll send you an email with instructions and the required documents to finalize your loan</p>
          :
          <p>Within the next 24 hours, we&apos;ll send you an email with instructions and the required documents to finalize this loan</p>
      }
    </div>
  </div>
)

export const InfoBox = () => (
  <div className='confirmation-box'>
    <div className='left'>
      <div className='icon'>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#6E7889" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M9.08984 9.00008C9.32495 8.33175 9.789 7.76819 10.3998 7.40921C11.0106 7.05024 11.7287 6.91902 12.427 7.03879C13.1253 7.15857 13.7587 7.52161 14.2149 8.06361C14.6712 8.60561 14.9209 9.2916 14.9198 10.0001C14.9198 12.0001 11.9198 13.0001 11.9198 13.0001" stroke="#6E7889" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 17H12.01" stroke="#6E7889" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
    <div className='right'>
      <p>Enter numbers for the values below and we&apos;ll give you some options. Don&apos;t worry if your numbers aren&apos;t perfect.</p>
    </div>
  </div>
)

const DownArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
    <path d="M11 1L6 6L1 1" stroke="#404859" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

interface LoanResumeMobileProps {
  onBack: () => void;
  onContinue: () => void;
  userClicked: boolean;
  isLoadingOAuth: boolean;
  documentsReady: boolean;
  [key: string]: any;
}

const LoanResumeMobile = (props: LoanResumeMobileProps) => {
  const [areDetailsOpen, setAreDetailsOpen] = useState(false);
  const { onBack, onContinue, userClicked, isLoadingOAuth, documentsReady } = props;

  const downloadDocuments = () => {
    const { mobileURL } = props;
    if (mobileURL) {
      window.open(mobileURL, '_blank', 'noopener,noreferrer');
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  return (
    <div className='loan-recap loan-recap-mobile'>
      <div className='mobile-container'>
        <div className='mobile-title-container'>
          <span className='tt'>
            {props.firstName
              ? `Congratulations ${props.firstName}, you've been pre-approved!`
              : "Congratulations, you've been pre-approved!"}
          </span>
        </div>
        <div className='mobile-content'>
          <FooterContent
            values={props.values}
            oneLine
            isSpan
            firstPerson
            isBrokerMode={props.isBrokerMode}
            forceUnlockCreditInfo
          />
          <Button
            onClick={onContinue}
            isLoading={userClicked && (isLoadingOAuth || !documentsReady)}
            disabled={false}
          >
            Continue Application
          </Button>

          <a className='button-documents-container button-documents-container--mb-12 hide-on-mobile' target={"_blank"} rel="noreferrer" href={props.preApprovalLetterURL}>
            Pre-approval letter
            <span className={"download"}>
              {"Download now  --> "}
            </span>
          </a>
          <a className='button-documents-container hide-on-mobile' target={"_blank"} rel="noreferrer" href={props.termSheetURL}>
            Term sheet
            <span className={"download"}>
              {"Download now  --> "}
            </span>
          </a>
          <a className='button-documents-container button-documents-container--mb-12 show-on-mobile' target={"_blank"} rel="noreferrer" href={props.preApprovalLetterURL}>
            Download pre-approval letter
          </a>
          <a className='button-documents-container show-on-mobile' target={"_blank"} rel="noreferrer" href={props.termSheetURL}>
            Download term sheet
          </a>

        </div>
        <div className='review-loan-details-title'><span>Review your loan details</span></div>
        <>
          <LoanInfoBox
            selectedOption={props.selectedOption}
            values={props.values}
            termSheetData={props.termSheetData}
          />
        </>

        {/* <div className='resume-container'> */}

        {/*           <div>
            <p className='view-details' onClick={() => setAreDetailsOpen(!areDetailsOpen)}>
              View Details <span className={!areDetailsOpen ? 'arrow' : 'arrow rotated'}><DownArrow /></span>
            </p>
            {
              areDetailsOpen ?
                <div className='input-container'>
                  {props.elements}
                </div>
                :
                null
            }
          </div> */}
        {/* </div> */}
      </div>
      {/* <ConfirmationBox /> */}
      {/* <DocumentsBox {...props} /> */}



    </div>
  )
}